import React, { useEffect, useMemo } from 'react';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { datadogRum } from '@datadog/browser-rum';

import { useAuth } from '@/contexts/auth-context';
import { useUserMeQuery } from '@/graphql';

const LS_KEY_GROWTHBOOK_ANON_ID = 'growthbookAnonymousId';

const getAnonymousId = () => {
  const existingAnonymousId = localStorage.getItem(LS_KEY_GROWTHBOOK_ANON_ID);
  if (existingAnonymousId) return existingAnonymousId;

  const generatedAnonymousId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  localStorage.setItem(LS_KEY_GROWTHBOOK_ANON_ID, generatedAnonymousId);
  return generatedAnonymousId;
};

const GrowthbookAppProvider: React.FC = ({ children }) => {
  const { isAuthorized, token } = useAuth();
  const { data } = useUserMeQuery({ skip: !token });

  const apiHost = import.meta.env.VITE_GROWTHBOOK_API_HOST;
  const clientKey = import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY;
  const decryptionKey = import.meta.env.VITE_GROWTHBOOK_DECRYPTION_KEY;

  const growthbook = useMemo(
    () =>
      new GrowthBook({
        // No keys (an environment where GrowthBook isn't set up): start with an empty
        // feature set so the SDK is immediately `ready`. Without it nothing ever marks
        // it ready, and <FeaturesReady> holds its fallback for the whole timeout —
        // 2s of skeleton on every landing page view.
        ...(apiHost && clientKey ? { apiHost, clientKey } : { features: {} }),
        ...(decryptionKey ? { decryptionKey } : {}),
        enableDevMode: import.meta.env.DEV,
        subscribeToChanges: true,
        trackingCallback: (experiment, result) => {
          datadogRum.addAction('growthbook_experiment_viewed', {
            experimentKey: experiment.key,
            variationId: result.key,
          });
        },
      }),
    [apiHost, clientKey, decryptionKey]
  );

  useEffect(() => {
    if (!(apiHost && clientKey)) return;

    growthbook.init({ timeout: 2000 }).catch((error) => {
      console.error('GrowthBook initialization failed', error);
    });

    return () => {
      growthbook.destroy();
    };
  }, [apiHost, clientKey, growthbook]);

  useEffect(() => {
    const anonymousId = getAnonymousId();

    growthbook.setAttributes({
      id: data?.me?.id || anonymousId,
      anonymousId,
      loggedIn: isAuthorized,
      professionType: data?.me?.profession?.type?.type,
      professionId: data?.me?.professionId,
    });
  }, [data?.me?.id, data?.me?.profession?.type?.type, data?.me?.professionId, growthbook, isAuthorized]);

  return <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>;
};

export default GrowthbookAppProvider;
